import { excludeFrom, isArray, type Nil } from "@mcwd/typescript-type-guards";
import type { AnyMarketoInputName, HiddenMarketoInputName, UserMarketoInputName } from "../../types/definitions/inputs/marketo-input-names-and-values.js";

namespace AllForms {
  export const Hidden = [
    "SiteLanguageCode",
    "FormName",
    "source",
    "Source_String__c",
    "Source_Website",
    "Medium__c",
    "Campaign__c",
    "Source__c",
    "Last_Offer_Campaign__c",
    "Last_Offer_Name__c",
    "Website_Language_Code__c",
    "GCLID__c",
    "Mobile__c",
    "experienceGatingState",
    "formGatingState",
    "initialFormGatingLevel",
    "initialFormExperienceGatingLevel",
  ] as const satisfies readonly HiddenMarketoInputName[];
}

namespace EnabledFieldsInstance {
  interface HiddenOnly {
    Hidden: readonly [... HiddenMarketoInputName[]]
  }

  interface PartialOnly {
    Partial: readonly [...(HiddenMarketoInputName | AnyMarketoInputName)[]]
  }

  interface FullOnly {
    Full: readonly [...(HiddenMarketoInputName | AnyMarketoInputName)[]]
  }

  interface HiddenAndFull extends HiddenOnly, FullOnly { }
  interface HiddenAndPartial extends HiddenOnly, PartialOnly { }
  interface All extends HiddenOnly, PartialOnly, FullOnly { }

  export type AnyValid = HiddenAndFull | HiddenAndPartial | All; 
}

// Overload without Partial
function createEnabledFieldsInstance<
  const THiddenFields extends readonly [...HiddenMarketoInputName[]],
  const TFullFields extends readonly [...AnyMarketoInputName[]]
>({ Hidden, Full }: { Hidden: THiddenFields, Full: TFullFields }): { Hidden: THiddenFields, Full: readonly [...THiddenFields, ...TFullFields] };

// Overload with all gating levels
function createEnabledFieldsInstance<
  const THiddenFields extends readonly [...HiddenMarketoInputName[]],
  const TFullFields extends readonly [...AnyMarketoInputName[]],
  const TPartialFields extends (readonly [...AnyMarketoInputName[]])
>({ Hidden, Partial, Full }: { Hidden: THiddenFields, Partial: TPartialFields, Full: TFullFields })
  : { Hidden: THiddenFields, Partial: readonly [...THiddenFields, ...TPartialFields], Full: readonly [...THiddenFields, ...TPartialFields, ...TFullFields] };

// Implementation function
function createEnabledFieldsInstance<
  const THiddenFields extends readonly [...HiddenMarketoInputName[]],
  const TFullFields extends readonly [...AnyMarketoInputName[]],
  const TPartialFields extends (readonly[...AnyMarketoInputName[]]) | Nil
>({ Hidden, Partial, Full }: { Hidden: THiddenFields, Partial?: TPartialFields, Full: TFullFields })
{
  if (isArray(Partial)) {
    // Add "Partial" if it is included in args
    const enabledFields = {
      Hidden,
      Partial: [...Hidden, ...Partial] as const, 
      Full: [...Hidden, ...Partial, ...Full] as const
    } as const;
    Object.freeze(enabledFields);
    return enabledFields;
  }
  else {
    const enabledFields = {
      Hidden,
      Full: [...Hidden, ...Full] as const
    } as const;
    Object.freeze(enabledFields);
    return enabledFields;
  }
}

type EnabledFieldsObject = {
  [key: string]: EnabledFieldsInstance.AnyValid
}

const ContactForm_FullGate = [
  "FirstName",
  "LastName",
  "Email",
  "Company",
  "Country",
  "State",
  "Phone",
  "Tier__c",
  "Industry",
  // "Department__c",
  "Email_Opt_In__c",
  "Webform_Comments_recent_value__c",
  "Latest_Solution_Interest__c",
] as const satisfies readonly UserMarketoInputName[];


export const EnabledFields = {
  Contact: createEnabledFieldsInstance({
    Hidden: AllForms.Hidden,
    Full: ContactForm_FullGate
  }),
  Resource: createEnabledFieldsInstance({
    Hidden: [
      ...AllForms.Hidden,
      "documentDownloadURL",
      "multiDocSelectorFormFill"
    ],
    Partial: [
      "FirstName",
      "LastName",
      "Email",
      "Company",
      "Email_Opt_In__c"
    ],
    Full: [
      "Country",
      "State",
      "Phone",
      "Tier__c",
      "Industry",
      // "Department__c",
    ]
  }),
  DemoRequest: createEnabledFieldsInstance({
    Hidden: AllForms.Hidden,
    Full: excludeFrom(ContactForm_FullGate, "Latest_Solution_Interest__c")
  }),
  Roi: createEnabledFieldsInstance({
    Hidden: [
      ...AllForms.Hidden,
      "roi_querystring__c_contact"
    ],
    Full: excludeFrom(ContactForm_FullGate, "Latest_Solution_Interest__c")
  }),
  Partner: createEnabledFieldsInstance({
    Hidden: AllForms.Hidden,
    Full: excludeFrom(ContactForm_FullGate, "Tier__c", "Industry", "Webform_Comments_recent_value__c", "Latest_Solution_Interest__c")
  }),
  TradeShow: createEnabledFieldsInstance({
    Hidden: AllForms.Hidden,
    Full: excludeFrom(ContactForm_FullGate, "Latest_Solution_Interest__c")
  }),
  Pricing: createEnabledFieldsInstance({
    Hidden: AllForms.Hidden,
    Full: excludeFrom(ContactForm_FullGate, "Webform_Comments_recent_value__c", "Latest_Solution_Interest__c")
  }),
  Subscription: createEnabledFieldsInstance({
    Hidden: AllForms.Hidden,
    Full: [
      "FirstName",
      "LastName",
      "Email",
      "Email_Opt_In__c",
    ]
  }),
  Subprocessor: createEnabledFieldsInstance({
    Hidden: AllForms.Hidden,
    Full: ["Email", "Company"]
  }),
  EventRegistration: createEnabledFieldsInstance({
    Hidden: AllForms.Hidden,
    Full: [
      ...excludeFrom(ContactForm_FullGate, "Webform_Comments_recent_value__c", "Latest_Solution_Interest__c"),
      "Partner_Opt_in_Processing__c",
    ]
  })
} as const satisfies EnabledFieldsObject;
